import { useEffect, useReducer, useRef, useState } from "react";

/**
 * Minimal toast system — Sonner principles, zero dependencies.
 *
 *   - Insert <Toaster /> once (in App); call toast() from anywhere. No context,
 *     no hooks at the call site.
 *   - CSS transitions (not keyframes) so rapid toasts retarget smoothly.
 *   - Enter from scale(0.95)+opacity (never scale(0)); ease-out custom curve.
 *   - Auto-dismiss after 4s, PAUSED while the tab is hidden (an operator who
 *     tabbed away still sees the confirmation when they return).
 *   - Click or wait to dismiss; exit animates before the node is removed.
 *   - prefers-reduced-motion: opacity only, no movement.
 */

type ToastKind = "success" | "error";
interface ToastRecord {
  id: number;
  message: string;
  kind: ToastKind;
}

let records: ToastRecord[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function push(message: string, kind: ToastKind): number {
  const id = nextId++;
  records = [...records, { id, message, kind }];
  emit();
  return id;
}

export const toast = {
  success: (message: string) => push(message, "success"),
  error: (message: string) => push(message, "error"),
};

function remove(id: number) {
  records = records.filter((r) => r.id !== id);
  emit();
}

const AUTO_DISMISS_MS = 4000;
const EXIT_MS = 220;

export function Toaster() {
  const [, force] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    listeners.add(force);
    return () => {
      listeners.delete(force);
    };
  }, []);

  if (records.length === 0) return null;

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      style={{
        position: "fixed",
        right: "20px",
        bottom: "20px",
        zIndex: 200,
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        maxWidth: "min(380px, calc(100vw - 40px))",
        pointerEvents: "none",
      }}
    >
      {records.map((r) => (
        <ToastItem key={r.id} record={r} />
      ))}
    </div>
  );
}

function ToastItem({ record }: { record: ToastRecord }) {
  const [shown, setShown] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const remaining = useRef(AUTO_DISMISS_MS);
  const startedAt = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const beginExit = () => {
    setLeaving(true);
    setTimeout(() => remove(record.id), EXIT_MS);
  };

  // Enter on next frame so the transition runs from the @starting state.
  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Auto-dismiss countdown that pauses while the tab is hidden.
  useEffect(() => {
    const startTimer = () => {
      startedAt.current = Date.now();
      timer.current = setTimeout(beginExit, remaining.current);
    };
    const pauseTimer = () => {
      if (timer.current) clearTimeout(timer.current);
      remaining.current -= Date.now() - startedAt.current;
    };
    const onVisibility = () => {
      if (document.hidden) pauseTimer();
      else startTimer();
    };
    if (!document.hidden) startTimer();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      if (timer.current) clearTimeout(timer.current);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isError = record.kind === "error";
  const open = shown && !leaving;

  return (
    <div
      role="status"
      onClick={beginExit}
      className="cspro-toast"
      data-open={open ? "true" : "false"}
      style={{
        pointerEvents: "auto",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "12px 14px",
        borderRadius: "12px",
        background: "var(--bg-2)",
        border: `1px solid ${isError ? "var(--rose)" : "var(--mint-rim)"}`,
        boxShadow: "0 16px 40px rgba(0,0,0,0.45)",
        color: "var(--ink)",
        fontFamily: "var(--sans)",
        fontSize: "13.5px",
        lineHeight: 1.45,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          flexShrink: 0,
          width: "8px",
          height: "8px",
          borderRadius: "50%",
          background: isError ? "var(--rose)" : "var(--mint)",
          boxShadow: isError ? "none" : "0 0 8px var(--mint-glow)",
        }}
      />
      <span>{record.message}</span>
    </div>
  );
}
